import { useEffect, React, useState, Suspense, useContext } from "react";
import useTabActivityMessage from "../hooks/usePageMetaTags";
import Auth from "../utils/AuthUtils";
import { formatDate, getPaymentStatusBadge } from "../helper/Helper";
import { Link } from "react-router-dom";
import SalesDayChart from "../utils/chart/SalesDayChart";
import orderAPI from "../api/orderApi";
import LowStockAlerts from "../components/partials/inventory/LowStockAlerts";
import { UserContext } from "../utils/UserContext";
import QRCode from "qrcode";
import { QrCode } from "lucide-react";

const Dashboard = () => {
  const [result, setResult] = useState("");
  const [adminName] = useState(Auth.getUserDetails("name"));
  const { user } = useContext(UserContext);
  const { storeName, websiteUrl } = user || {};

  useEffect(() => {
    document.title = "Dashboard | AxeSell Ecommerce Solution";
  }, []);

  useTabActivityMessage("Dashboard | AxeSell Ecommerce Solution");

  const [range, setRange] = useState("1M");

  useEffect(() => {
    fetchDashboardData(range);
  }, [range]);

  const fetchDashboardData = async (range) => {
    try {
      const res = await orderAPI.homepage({ range });

      if (res.code === 200) {
        setResult(res.data);
      }
    } catch (err) {}
  };

  const handleRangeClick = (selectedRange) => {
    setRange(selectedRange);
  };

  const downloadQRCode = async (websiteUrl, fileName = "qrcode") => {
    try {
      const qrDataUrl = await QRCode.toDataURL(websiteUrl, {
        width: 500,
        margin: 2,
        color: {
          dark: "#000000",
          light: "#FFFFFF",
        },
        errorCorrectionLevel: "H",
      });

      const link = document.createElement("a");
      link.href = qrDataUrl;
      link.download = `${fileName}.png`;
      link.click();
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <>
      {/* /Sidebar */}
      <div className="content dashboard">
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
          <div className="mb-3">
            <h2 className="mb-1">Welcome, {adminName}</h2>
            <p className="fw-medium">
              You have
              <span className="text-primary fw-medium">
                {" "}
                {result?.todayOrders ?? 0}
              </span>{" "}
              Orders, Today
            </p>
          </div>

          <div className="page-btn">
            <button
              className="btn btn-secondary"
              onClick={() => downloadQRCode(websiteUrl, `${storeName} qrcode`)}
            >
              <QrCode size={25} /> <br /> Download QR
            </button>
          </div>
        </div>

        {result?.lowStockAlerts && result.lowStockAlerts.length > 0 && (
          <>
            <Suspense fallback={<div></div>}>
              <LowStockAlerts
                alerts={result.lowStockAlerts}
                onRefresh={fetchDashboardData}
              />
            </Suspense>
          </>
        )}

        <div className="row">
          <div className="col-xl-3 col-sm-6 col-12 d-flex">
            <div className="card bg-primary sale-widget flex-fill">
              <div className="card-body d-flex align-items-center">
                <span className="sale-icon bg-white text-primary">
                  <i className="ti ti-report-money fs-24" />
                </span>
                <div className="ms-2">
                  <p className="text-white mb-1">Total Revenue</p>
                  <div className="d-inline-flex align-items-center flex-wrap gap-2">
                    <h4 className="text-white">
                      ₹{result?.totalRevenue?.toLocaleString("en-IN") ?? 0}
                    </h4>
                    <span className="badge badge-soft-primary">
                      {Number(result?.processingOrders ?? "0") +
                        Number(result?.deliveredOrders ?? "0") +
                        Number(result?.shippedOrders ?? "0")}{" "}
                      Orders
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-xl-3 col-sm-6 col-12 d-flex">
            <div className="card bg-secondary sale-widget flex-fill">
              <div className="card-body d-flex align-items-center">
                <span className="sale-icon bg-white text-secondary">
                  <i className="ti ti-repeat fs-24" />
                </span>
                <div className="ms-2">
                  <p className="text-white mb-1">Orders In Processing</p>
                  <div className="d-inline-flex align-items-center flex-wrap gap-2">
                    <h4 className="text-white">
                      {result.processingOrders ?? 0}
                    </h4>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-xl-3 col-sm-6 col-12 d-flex">
            <div className="card bg-teal sale-widget flex-fill">
              <div className="card-body d-flex align-items-center">
                <span className="sale-icon bg-white text-teal">
                  <i className="ti ti-gift fs-24" />
                </span>
                <div className="ms-2">
                  <p className="text-white mb-1">Shipped Orders</p>
                  <div className="d-inline-flex align-items-center flex-wrap gap-2">
                    <h4 className="text-white">
                      {result.deliveredOrders ?? 0}
                    </h4>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-xl-3 col-sm-6 col-12 d-flex">
            <div className="card bg-info sale-widget flex-fill">
              <div className="card-body d-flex align-items-center">
                <span className="sale-icon bg-white text-info">
                  <i className="ti ti-brand-pocket fs-24" />
                </span>
                <div className="ms-2">
                  <p className="text-white mb-1">Delivered Orders</p>
                  <div className="d-inline-flex align-items-center flex-wrap gap-2">
                    <h4 className="text-white">{result.shippedOrders ?? 0}</h4>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="row">
          {/* Sales & Purchase */}
          <div className="col-xxl-8 col-xl-7 col-sm-12 col-12 d-flex">
            <div className="card flex-fill">
              <div className="card-header d-flex justify-content-between align-items-center">
                <div className="d-inline-flex align-items-center">
                  <span className="title-icon bg-soft-primary fs-16 me-2">
                    <i className="ti ti-shopping-cart" />
                  </span>
                  <h5 className="card-title mb-0">Sales Report</h5>
                </div>
                <ul className="nav btn-group custom-btn-group">
                  {["1D", "1W", "1M", "3M", "6M", "1Y"].map((r) => (
                    <span
                      key={r}
                      className={`btn btn-outline-light ${
                        range === r ? "active" : ""
                      }`}
                      onClick={() => handleRangeClick(r)}
                    >
                      {r}
                    </span>
                  ))}
                </ul>
              </div>
              <div className="card-body pb-0">
                <SalesDayChart details={result?.salesSummaryChart} />
              </div>
            </div>
          </div>
          {/* /Sales & Purchase */}

          {/* Top Selling Products */}
          {result?.topCustomers && (
            <div className="col-xxl-4 col-md-5 d-flex">
              <div className="card flex-fill">
                <div className="card-header d-flex justify-content-between align-items-center flex-wrap gap-3">
                  <div className="d-inline-flex align-items-center">
                    <span className="title-icon bg-soft-orange fs-16 me-2">
                      <i className="ti ti-users" />
                    </span>
                    <h5 className="card-title mb-0">Top Customers</h5>
                  </div>
                  <Link
                    to="/user"
                    className="fs-13 fw-medium text-decoration-underline"
                  >
                    View All
                  </Link>
                </div>
                <div className="card-body">
                  <Suspense fallback={<div></div>}>
                    {result?.topCustomers?.map((item, index) => (
                      <div
                        className="d-flex align-items-center justify-content-between border-bottom mb-2 pb-2 flex-wrap gap-2"
                        key={item._id}
                      >
                        <div className="d-flex align-items-center">
                          <div>
                            <h6 className="fs-14 fw-medium mb-1">
                              <Link to={`/customer/details/${item?._id}`}>
                                {item?.name}
                              </Link>
                            </h6>
                            <div className="d-flex align-items-center item-list">
                              <p className="d-inline-flex align-items-center">
                                <i className="ti ti-map-pin me-1" />
                                {item?.state}
                              </p>
                              <p>{item?.orders} Orders</p>
                            </div>
                          </div>
                        </div>
                        <div className="text-end">
                          <p>₹{item?.revenue?.toLocaleString("en-IN")}</p>
                        </div>
                      </div>
                    ))}
                  </Suspense>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="row">
          {/* Top Selling Products */}

          {result.topProducts && (
            <div className="col-xxl-4 col-md-6 d-flex">
              <div className="card flex-fill">
                <div className="card-header d-flex justify-content-between align-items-center flex-wrap gap-3">
                  <div className="d-inline-flex align-items-center">
                    <span className="title-icon bg-soft-pink fs-16 me-2">
                      <i className="ti ti-box" />
                    </span>
                    <h5 className="card-title mb-0">Top Selling Products</h5>
                  </div>
                </div>
                <div className="card-body sell-product">
                  <Suspense fallback={<div></div>}>
                    {result.topProducts.map((item, index) => (
                      <div
                        className={`d-flex align-items-center border-bottom`}
                        key={item._id}
                      >
                        <span className="avatar avatar-lg">
                          <img src={item?.thumbnail} alt="img" />
                        </span>
                        <div className="ms-2">
                          <h6 className="fw-medium mb-1">
                            {item._id}
                          </h6>
                          <div className="d-flex align-items-center item-list">
                            <p>₹{item?.revenue?.toLocaleString("en-IN")}</p>
                            <p>{item?.qty} Quantity</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </Suspense>
                </div>
              </div>
            </div>
          )}

          {/* Low Stock Products */}

          {result?.locationStats?.byState && (
            <div className="col-xxl-4 col-md-6 d-flex">
              <div className="card flex-fill">
                <div className="card-header d-flex justify-content-between align-items-center flex-wrap gap-3">
                  <div className="d-inline-flex align-items-center">
                    <span className="title-icon bg-soft-danger fs-16 me-2">
                      <i className="ti ti-box" />
                    </span>
                    <h5 className="card-title mb-0">State Wise Records</h5>
                  </div>
                </div>
                <div className="card-body">
                  <Suspense fallback={<div></div>}>
                    {result?.locationStats?.byState?.map((item, index) => (
                      <div key={index}>
                        <div className="border br-8 mb-1">
                          <div className="d-flex align-items-center item-list fw-medium p-2">
                            <p>{item.name}</p>
                            <p>
                              ₹{item?.totalRevenue?.toLocaleString("en-IN")}
                            </p>
                            <p>{item?.totalOrders} Orders</p>
                          </div>
                        </div>

                        {item.topProducts?.map((items, index) => (
                          <div
                            className="mb-2 pb-2 d-flex align-items-center justify-content-between border-bottom"
                            key={index}
                          >
                            <div className="d-flex align-items-center">
                              <span className="avatar avatar-lg">
                                <img src={items.thumbnail} alt="img" />
                              </span>
                              <div className="ms-2">
                                <h6 className="fw-medium mb-1">
                                  <span>{items.productName}</span>
                                </h6>
                                <div className="d-flex align-items-center item-list">
                                  <p>
                                    ₹{items?.revenue?.toLocaleString("en-IN")}
                                  </p>
                                  <p>{items?.orders} Orders</p>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ))}
                  </Suspense>
                </div>
              </div>
            </div>
          )}

          {result?.locationStats?.byDistrict && (
            <div className="col-xxl-4 col-md-6 d-flex">
              <div className="card flex-fill">
                <div className="card-header d-flex justify-content-between align-items-center flex-wrap gap-3">
                  <div className="d-inline-flex align-items-center">
                    <span className="title-icon bg-soft-danger fs-16 me-2">
                      <i className="ti ti-box" />
                    </span>
                    <h5 className="card-title mb-0">District Wise Records</h5>
                  </div>
                </div>
                <div className="card-body">
                  <Suspense fallback={<div></div>}>
                    {result?.locationStats?.byDistrict?.map((item, index) => (
                      <div className="mb-3" key={index}>
                        <div className="border br-8 mb-1">
                          <div className="d-flex align-items-center item-list fw-medium p-2">
                            <p>{item.name}</p>
                            <p>
                              ₹{item?.totalRevenue?.toLocaleString("en-IN")}
                            </p>
                            <p>{item?.totalOrders} Orders</p>
                          </div>
                        </div>

                        {item.topProducts?.map((items, index) => (
                          <div
                            className="mb-2 pb-2  d-flex align-items-center justify-content-between border-bottom"
                            key={index}
                          >
                            <div className="d-flex align-items-center">
                              <span className="avatar avatar-lg">
                                <img src={items.thumbnail} alt="img" />
                              </span>
                              <div className="ms-2">
                                <h6 className="fw-medium mb-1">
                                  <span>{items.productName}</span>
                                </h6>
                                <div className="d-flex align-items-center item-list">
                                  <p>
                                    ₹{items?.revenue?.toLocaleString("en-IN")}
                                  </p>
                                  <p>{items?.orders} Orders</p>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ))}
                  </Suspense>
                </div>
              </div>
            </div>
          )}
        </div>

        {result?.recentTransactions && (
          <div className="row">
            <div className="col-xl-12 col-sm-12 col-12 d-flex">
              <div className="card flex-fill">
                <div className="card-header d-flex align-items-center justify-content-between flex-wrap gap-3">
                  <div className="d-inline-flex align-items-center">
                    <span className="title-icon bg-soft-orange fs-16 me-2">
                      <i className="ti ti-flag" />
                    </span>
                    <h5 className="card-title mb-0">Recent Orders</h5>
                  </div>
                </div>
                <div className="card-body p-0">
                  <div className="table-responsive">
                    <table className="table table-borderless">
                      <thead className="thead-light">
                        <tr>
                          <th>Date</th>
                          <th>Customer</th>
                          <th>Payment Details</th>
                          <th>Status</th>
                          <th>Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        <Suspense fallback={<div></div>}>
                          {result?.recentTransactions?.map((item, index) => (
                            <tr key={index}>
                              <td>
                                <span className="fs-13">
                                  {formatDate(item.createdAt)}
                                </span>
                              </td>
                              <td>
                                <div className="d-flex align-items-center file-name-icon">
                                  <div className="ms-2">
                                    <h6>
                                      <Link
                                        to={`/customer/details/${item?.userId?._id}`}
                                        className="fw-medium"
                                      >
                                        {item?.userId?.firstName ?? ""}{" "}
                                        {item?.userId?.lastName ?? ""}
                                      </Link>
                                    </h6>
                                    <span className="fs-13 text-primary">
                                      <a href={`mailto: ${item.userId?.email}`}>
                                        {item.userId?.email}
                                      </a>
                                    </span>
                                  </div>
                                </div>
                              </td>
                              <td>
                                {item?.payment && (
                                  <>
                                    <span className="fs-13">
                                      {item?.payment?.method}
                                    </span>
                                    <br />
                                    <span
                                      className={`badge ${getPaymentStatusBadge(
                                        item?.payment?.status,
                                      )}`}
                                    >
                                      {item?.payment?.status}
                                    </span>
                                  </>
                                )}
                              </td>
                              <td>
                                {item?.payment?.details && (
                                  <>
                                    <span className="fs-13">
                                      {
                                        item?.payment?.details
                                          ?.razorpay_order_id
                                      }
                                    </span>
                                    <br />
                                    <span className="fs-13">
                                      {
                                        item?.payment?.details
                                          ?.razorpayPaymentId
                                      }
                                    </span>
                                  </>
                                )}
                              </td>
                              <td className="fs-16 text-gray-9">
                                ₹{item?.total?.toLocaleString("en-IN")}
                              </td>
                            </tr>
                          ))}
                        </Suspense>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default Dashboard;
